import { apiClient } from './client';

// User Types
export interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'staff' | 'customer' | 'callcenter_admin';
  is_active: boolean;
  team_id?: number;
  team_name?: string;
  created_at: string;
  updated_at?: string;
  last_login?: string;
  profile_completed: boolean;
}

export interface UserCreate {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  role: 'admin' | 'staff' | 'customer' | 'callcenter_admin';
  is_active?: boolean;
  team_id?: number;
  // profile
  date_of_birth?: string;
  national_id?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  // callcenter extras
  applied_bank?: string;
  applied_amount?: number;
  offered_interest_rate?: number;
  application_website?: string;
  // joint account
  is_joint_account?: boolean;
  partner_first_name?: string;
  partner_last_name?: string;
  partner_birth_date?: string;
  partner_national_id?: string;
  partner_address?: string;
  partner_city?: string;
  partner_postal_code?: string;
  partner_country?: string;
}

export interface UserUpdate {
  first_name?: string;
  last_name?: string;
  phone?: string;
  role?: 'admin' | 'staff' | 'customer' | 'callcenter_admin';
  is_active?: boolean;
  team_id?: number;
  // profile fields
  date_of_birth?: string;
  national_id?: string;
  address?: string;
  city?: string;
  country?: string;
  postal_code?: string;
  // callcenter extras
  applied_bank?: string;
  applied_amount?: number;
  offered_interest_rate?: number;
  application_website?: string;
  // joint account
  is_joint_account?: boolean;
  partner_first_name?: string;
  partner_last_name?: string;
  partner_birth_date?: string;
  partner_national_id?: string;
  partner_address?: string;
  partner_city?: string;
  partner_postal_code?: string;
  partner_country?: string;
}

export interface UserListResponse {
  users: User[];
  total: number;
  page: number;
  page_size: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface UserSearchParams {
  page?: number;
  page_size?: number;
  role?: 'admin' | 'staff' | 'customer' | 'callcenter_admin' | string;
  is_active?: boolean;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  team_id?: number;
}

// User API Service
export const userApi = {
  // List users with pagination and filtering
  async list(params: UserSearchParams = {}): Promise<UserListResponse> {
    try {
      const response = await apiClient.get('/api/v1/users', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw new Error('Kullanıcılar getirilemedi');
    }
  },

  // Get user by ID
  async getById(id: number): Promise<User> {
    try {
      const response = await apiClient.get(`/api/v1/users/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw new Error('Kullanıcı bulunamadı');
    }
  },

  // Create new user
  async create(userData: UserCreate): Promise<User> {
    try {
      const response = await apiClient.post('/api/v1/users', userData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating user:', error);
      const message = error.response?.data?.detail || 'Kullanıcı oluşturulamadı';
      throw new Error(message);
    }
  },

  // Update user
  async update(id: number, userData: UserUpdate): Promise<User> {
    try {
      const response = await apiClient.put(`/api/v1/users/${id}`, userData);
      return response.data;
    } catch (error: any) {
      console.error('Error updating user:', error);
      const message = error.response?.data?.detail || 'Kullanıcı güncellenemedi';
      throw new Error(message);
    }
  },

  // Toggle user status (activate/deactivate)
  async toggleStatus(id: number): Promise<User> {
    try {
      // First get current user to check status
      const currentUser = await this.getById(id);
      const endpoint = currentUser.is_active ? 'deactivate' : 'activate';
      
      const response = await apiClient.post(`/api/v1/users/${id}/${endpoint}`);
      return response.data;
    } catch (error: any) {
      console.error('Error toggling user status:', error);
      const message = error.response?.data?.detail || 'Kullanıcı durumu değiştirilemedi';
      throw new Error(message);
    }
  },

  // Delete user
  async delete(id: number): Promise<void> {
    try {
      await apiClient.delete(`/api/v1/users/${id}`);
    } catch (error: any) {
      console.error('Error deleting user:', error);
      const message = error.response?.data?.detail || 'Kullanıcı silinemedi';
      throw new Error(message);
    }
  },

  // Get user statistics
  async getStats(): Promise<any> {
    try {
      const response = await apiClient.get('/api/v1/users/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw new Error('İstatistikler getirilemedi');
    }
  },

  // Search users
  async search(query: string, filters: any = {}): Promise<User[]> {
    try {
      const params = { search: query, ...filters };
      const response = await apiClient.get('/api/v1/users/search', { params });
      return response.data.users || [];
    } catch (error) {
      console.error('Error searching users:', error);
      throw new Error('Arama yapılamadı');
    }
  }
}; 